import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiAlertTriangle, FiDollarSign } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { clientesService } from '../../services';

const ClienteEstadoCuenta = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [estadoCuenta, setEstadoCuenta] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState('2024-01-01');
  const [dateTo, setDateTo] = useState('2024-03-31');

  useEffect(() => {
    loadFacturas();
  }, [id]);

  const loadFacturas = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Obtener estado de cuenta desde la API
      const result = await clientesService.getEstadoCuenta(id, {
        date_from: dateFrom,
        date_to: dateTo
      });

      if (result.success) {
        setEstadoCuenta(result.data);
      } else {
        setError(result.message || 'Error al cargar el estado de cuenta');
      }
    } catch (error) {
      console.error('Error cargando estado de cuenta:', error);
      setError('Error de conexión al cargar el estado de cuenta');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePagar = (transaction) => {
    if (transaction.invoice_id) {
      navigate(`/system/clientes/${id}/pagar?factura=${transaction.invoice_id}`);
    }
  };

  if (isLoading) {
    return <LoadingGrid count={3} message="Cargando estado de cuenta..." />;
  }

  return (
    <div className="cliente-estado-cuenta-page">
      {/* Header */}
      <div className="page-header-simple">
        <button onClick={() => navigate(`/system/clientes/${id}`)} className="btn btn--ghost">
          <FiArrowLeft /> Volver
        </button>
        <h1 className="page-title">ESTADO DE CUENTA</h1>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {!estadoCuenta ? (
        <div className="empty-state">
          <p>No se pudo cargar el estado de cuenta</p>
        </div>
      ) : (
        <>
          {/* Información del Cliente */}
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.1em' }}>{estadoCuenta.client_name}</h3>
            <div style={{ fontSize: '0.9em', color: '#6b7280' }}>
              Período: {new Date(estadoCuenta.period?.from).toLocaleDateString('es-CO')} - {new Date(estadoCuenta.period?.to).toLocaleDateString('es-CO')}
            </div>
          </div>

          {/* Alertas */}
          {estadoCuenta.summary?.overdue_balance > 0 && (
            <div className="alert alert--warning">
              <FiAlertTriangle />
              <div>
                <strong>Saldo Vencido:</strong> ${estadoCuenta.summary.overdue_balance?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}
              </div>
            </div>
          )}

          {/* Resumen */}
          <div className="estado-cuenta-summary">
            <div className="summary-card">
              <div className="summary-label">Total Facturado</div>
              <div className="summary-value">${estadoCuenta.summary?.total_invoiced?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Pagado</div>
              <div className="summary-value">${estadoCuenta.summary?.total_paid?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
            </div>
          </div>

          <div className="estado-cuenta-summary" style={{ marginTop: '10px' }}>
            <div className="summary-card warning">
              <div className="summary-label">Saldo Actual</div>
              <div className="summary-value">${estadoCuenta.summary?.current_balance?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Saldo del Período</div>
              <div className="summary-value">${estadoCuenta.summary?.period_balance?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
            </div>
          </div>

          {/* Información de Crédito */}
          {estadoCuenta.credit_info && (
            <div style={{ marginTop: '20px' }}>
              <h3 className="section-title">Información de Crédito</h3>
              <div className="estado-cuenta-summary">
                <div className="summary-card">
                  <div className="summary-label">Límite de Crédito</div>
                  <div className="summary-value">${estadoCuenta.credit_info.credit_limit?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="summary-card">
                  <div className="summary-label">Crédito Disponible</div>
                  <div className="summary-value" style={{ color: estadoCuenta.credit_info.credit_available > 0 ? '#10b981' : '#ef4444' }}>
                    ${estadoCuenta.credit_info.credit_available?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-label">Términos de Pago</div>
                  <div className="summary-value">{estadoCuenta.credit_info.payment_terms} días</div>
                </div>
              </div>
            </div>
          )}

          {/* Lista de Transacciones */}
          <div style={{ marginTop: '20px' }}>
            <h3 className="section-title">Transacciones del Período</h3>
            {!estadoCuenta.transactions || estadoCuenta.transactions.length === 0 ? (
              <div className="empty-state">
                <p>No hay transacciones en este período</p>
              </div>
            ) : (
              <div className="facturas-list">
                {estadoCuenta.transactions.map((transaction, index) => (
                  <div
                    key={index}
                    className={`factura-card ${transaction.is_overdue ? 'vencida' : ''}`}
                  >
                    <div className="factura-header">
                      <div className="factura-numero">{transaction.invoice_number || transaction.reference}</div>
                      {transaction.is_overdue && (
                        <span className="badge-vencida">
                          <FiAlertTriangle /> Vencida
                        </span>
                      )}
                    </div>

                    <div className="factura-body">
                      <div className="factura-fechas">
                        <div className="fecha-item">
                          <span className="label">Fecha:</span>
                          <span>{new Date(transaction.date).toLocaleDateString('es-CO')}</span>
                        </div>
                        <div className="fecha-item">
                          <span className="label">Tipo:</span>
                          <span>{transaction.type || 'Factura'}</span>
                        </div>
                      </div>

                      {transaction.description && (
                        <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#6b7280' }}>
                          {transaction.description}
                        </div>
                      )}
                    </div>

                    <div className="factura-footer">
                      <div className="factura-total">
                        <span className="label">Monto:</span>
                        <span className="monto" style={{ color: transaction.amount < 0 ? '#ef4444' : '#10b981' }}>
                          ${Math.abs(transaction.amount || 0).toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      {transaction.balance !== undefined && (
                        <div style={{ fontSize: '0.85em', color: '#6b7280' }}>
                          Saldo: ${transaction.balance?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                        </div>
                      )}
                      {transaction.invoice_id && transaction.payment_status === 'unpaid' && (
                        <button
                          onClick={() => handlePagar(transaction)}
                          className="btn btn--primary btn--small"
                        >
                          <FiDollarSign /> Pagar
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Menú inferior */}
      <BottomTabMenu baseRoute={`/system/clientes/${id}`} currentTab="estado-cuenta" />
    </div>
  );
};

export default ClienteEstadoCuenta;
